import type { CardPart } from './chatReducer';
import type { CardAction, Claim, FactInfo, Pack } from './types';

export type CiteChip = { id: string; fact: FactInfo | null };

/**
 * A claim's chips. A cite missing from the pack's `facts` (possible on an
 * unreviewed pack) has no fact and is shown as plain text (Plan 4a).
 */
export const citeChips = (
  claim: Claim,
  facts: Record<string, FactInfo>,
): CiteChip[] =>
  claim.cites.map((id) => ({
    id,
    fact: Object.prototype.hasOwnProperty.call(facts, id) ? facts[id] : null,
  }));

export type ButtonState = 'hidden' | 'enabled' | 'disabled';

/** Hidden when the card doesn't offer the action; enabled only while the card is open. */
export function buttonState(card: CardPart, action: CardAction): ButtonState {
  if (!card.actions.includes(action)) return 'hidden';
  // An incomplete pack must never be approvable, whatever the card says. A trial pack
  // has no gate either: the server sends no actions, this is belt and braces.
  if (
    (card.pack?.incomplete === true || card.pack?.trial === true) &&
    (action === 'approve' || action === 'reject')
  )
    return 'hidden';
  return card.cardState === 'open' ? 'enabled' : 'disabled';
}

export type PackBadge = { type: 'error' | 'warning' | 'info'; text: string };

/** The pack's banners (Plan 4a "Badges and banners"). */
export function packBadges(pack: Pack): PackBadge[] {
  const badges: PackBadge[] = [];
  if (pack.incomplete) {
    badges.push({
      type: 'error',
      text: 'Incomplete: the crew stopped before a full pack, so there is nothing to approve.',
    });
  }
  // An incomplete pack is always flagged unreviewed; one banner says it. A pack with no
  // eligible lender is unreviewed because there was nothing to review; it says that instead.
  if (pack.trial) {
    badges.push({
      type: 'warning',
      text: "Trial — draft skills: this pack was made with your draft skills, so it can't be approved or rejected.",
    });
  }
  if (pack.noEligibleLender && !pack.incomplete) {
    badges.push({
      type: 'warning',
      text: 'No eligible lender: the EMS Agent found no lender that fits, so the CA Agent and Compliance did not run. Its reasons and the missing inputs are below.',
    });
  } else if (pack.unreviewed && !pack.incomplete) {
    // The checks ran out of retries before Compliance was asked: it never saw the pack.
    const neverReviewed = pack.review === null && pack.checkFixes.length > 0;
    badges.push({
      type: 'warning',
      text: neverReviewed
        ? 'Unreviewed: the code checks still found problems after 2 retries, so Compliance did not review it. You can still approve it.'
        : 'Unreviewed: Compliance did not pass it within 2 rounds. You can still approve it.',
    });
  }
  if (!pack.independentReview) {
    badges.push({
      type: 'info',
      text: 'Reviewer on the same vendor (temporary config).',
    });
  }
  return badges;
}
